// src/components/clients/ClientEditModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Row, Col, Divider } from 'antd';
import { updateClient } from '../../services/clientService';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;

const ClientEditModal = ({ visible, onClose, onClientUpdated, client }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Pre-fill form when client data changes
    useEffect(() => {
        if (client && visible) {
            form.setFieldsValue({
                razon_social: client.razon_social,
                condicion_impositiva: client.condicion_impositiva,
                direccion: client.direccion || '',
                localidad: client.localidad || '',
                provincia: client.provincia || '',
                codigopostal: client.codigopostal || '',
                email: client.email || '',
                telefono: client.telefono || '',
                estado: client.estado || 'ACTIVO'
            });
        }
    }, [client, visible, form]);

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Prepare update data (only fields that can be updated)
            const updateData = {
                razon_social: values.razon_social,
                condicion_impositiva: values.condicion_impositiva,
                direccion: values.direccion || '',
                localidad: values.localidad || '',
                provincia: values.provincia || '',
                codigopostal: values.codigopostal || '',
                email: values.email || '',
                telefono: values.telefono || '',
                estado: values.estado
            };

            const response = await updateClient(client.id, updateData);

            if (response.success) {
                message.success('Cliente actualizado exitosamente');
                onClientUpdated(response.client);
                onClose();
            }
        } catch (error) {
            console.error('Error updating client:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                message.error(errorData.message || 'Error al actualizar cliente');
            } else {
                message.error('Error al actualizar cliente');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Format CUIT with hyphens for display
    const formatCuit = (cuit) => {
        if (!cuit) return '';
        const cleaned = cuit.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
        }
        return cuit;
    };

    return (
        <Modal
            title="Editar Cliente"
            open={visible}
            onCancel={handleCancel}
            width={700}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={isDemoMode}
                >
                    Actualizar Cliente
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
            >
                <Divider orientation="left">CUIT (No modificable)</Divider>
                
                <Form.Item label="CUIT">
                    <Input
                        value={formatCuit(client?.documento_nro)}
                        disabled
                        style={{ backgroundColor: '#f5f5f5' }}
                    />
                </Form.Item>

                <Divider orientation="left">Información del Cliente</Divider>

                <Form.Item
                    name="razon_social"
                    label="Razón Social"
                    rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
                >
                    <Input placeholder="Empresa SA" />
                </Form.Item>

                <Form.Item
                    name="condicion_impositiva"
                    label="Condición Impositiva"
                    rules={[{ required: true, message: 'Por favor seleccione la condición impositiva' }]}
                >
                    <Select placeholder="Seleccione condición impositiva">
                        <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                        <Option value="MONOTRIBUTO">Monotributo</Option>
                        <Option value="EXENTO">Exento</Option>
                        <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                    </Select>
                </Form.Item>

                <Divider orientation="left">Datos de Contacto</Divider>

                <Form.Item
                    name="direccion"
                    label="Dirección"
                >
                    <Input placeholder="Av. Corrientes 1234" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="localidad"
                            label="Localidad"
                        >
                            <Input placeholder="CABA" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="provincia"
                            label="Provincia"
                        >
                            <Input placeholder="Buenos Aires" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="codigopostal"
                            label="Código Postal"
                        >
                            <Input placeholder="1043" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="telefono"
                            label="Teléfono"
                        >
                            <Input placeholder="011-1234-5678" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[{ type: 'email', message: 'Email inválido' }]}
                        >
                            <Input placeholder="email@ejemplo.com" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Estado</Divider>

                <Form.Item
                    name="estado"
                    label="Estado del Cliente"
                    rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
                >
                    <Select placeholder="Seleccione estado">
                        <Option value="ACTIVO">Activo</Option>
                        <Option value="INACTIVO">Inactivo</Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ClientEditModal;
